import { compose, mdsWidget } from 'dojo/_base/declare';
import template from './evisio2workspaceRoot.html?raw';
import lang from 'dojo/_base/lang';

import _WidgetBaseSP from 'more/_WidgetBaseSP';
import evisio2IframeWorkspaceApp from './evisio2IframeWorkspaceApp/evisio2IframeWorkspaceApp';
import evisio2Companion from './evisio2Companion/evisio2Companion';
import * as Evisio2SdkLoader from "./sdk/evisio2SdkLoader";
import Evisio2SdkWiring from './sdk/evisio2SdkWiring';
import * as Evisio2ServerConfigCheck from "./evisio2ServerConfigCheck";

// Dev-only mock SDK + simulator. Eagerly imported so the dojo-webpack-plugin
// registers them in the AMD module graph — lazy require would resolve to
// `undefinedModule` because nothing else in the project references them. Runtime
// cost is zero outside mock mode (instances only constructed inside _bootMock).
import { MockEvisio2Sdk } from "./dev/mockSdk/mockEvisio2Sdk";
import Evisio2SdkSimulator from './dev/simulator/evisio2SdkSimulator';

// Dev-only mock SDK simulator engagement. Gated behind `_DEV` role; toggled via F1
// which flips `sessionStorage.evisio2_mockSdk` and reloads. The flag is per-tab
// (sessionStorage) and clears automatically on tab close so no session can engage
// mock mode without an explicit toggle by a dev-role-holding user.
const MOCK_SDK_FLAG_KEY = "evisio2_mockSdk";

class evisio2workspaceRoot extends compose(_WidgetBaseSP) {
    templateString: any = template;


    continueAtPath: string = "";

    private iframeWidgetContainer:HTMLDivElement;
    private WorkspaceCore: MdsLax<InstanceType<typeof evisio2IframeWorkspaceApp>>|null = null;

    private companionContainer:HTMLDivElement;
    private companionWidget: MdsLax<InstanceType<typeof evisio2Companion>>|null = null;

    // Bridge ↔ SDK wiring layer. Constructed once the MDSC-signed-in AI iframe element
    // is known (see _onIframeSignedIn); torn down on destroy.
    private sdkWiring: Evisio2SdkWiring | null = null;

    // Dev-only — populated when `_DEV` role check resolves true. Removed in destroy.
    private _mockSdkHotkeyHandler: ((e: KeyboardEvent) => void) | null = null;

    // Dev-only — populated by _bootMock so destroy() can tear it down even though it
    // doesn't live in this.WorkspaceCore.
    private _simulatorWidget: any = null;

    postCreate(): void {
        let self: any = this;
        super.postCreate.apply(this, arguments as any);

        this.setTitles(this.getTranslation("Evisio2_title"));

        if (this.isInTeamsTab()){
            let blockedTxt = this.getTranslation("Evisio2_NotAvailableInTeams");
            let blockedWrapper = document.createElement("div");
            blockedWrapper.className = "noAccessMessage";
            let fancyH1 = document.createElement("h1");
            fancyH1.innerHTML = blockedTxt;
            blockedWrapper.appendChild(fancyH1);
            let backLink = document.createElement("a");
            backLink.href = "/TeamsIntegration";
            backLink.innerHTML = this.getTranslation("Evisio2_ReturnToTeamsTab");
            blockedWrapper.appendChild(backLink);
            this.iframeWidgetContainer.appendChild(blockedWrapper);
            return;
        }

        let mockFlagSet = false;
        try { mockFlagSet = sessionStorage.getItem(MOCK_SDK_FLAG_KEY) === "1"; }
        catch { /* sessionStorage may be inaccessible in some embed contexts — fall through to real boot */ }

        // Real-boot kickoff fires eagerly so the SDK fetch overlaps with the role-check POST.
        // Skipped when the mock flag is set — the mock path doesn't need the real SDK,
        // and skipping avoids the CDN script overwriting window.evisio2sdk after we've
        // installed the mock there.
        if (!mockFlagSet){
            Evisio2SdkLoader.kickoff();
        }

        // _DEV role check — installs the hotkey listener and decides whether to take
        // the mock branch (if flag set + role present) or fall back to real boot.
        this.hasUserRoleAsync("_DEV").then((hasDev: boolean) => {
            if (hasDev) this._installMockSdkHotkey();

            if (mockFlagSet && hasDev){
                this._bootMock();
                return;
            }
            if (mockFlagSet && !hasDev){
                // Stale flag — clear it and proceed with real boot. The kickoff was
                // deferred above so fire it now.
                try { sessionStorage.removeItem(MOCK_SDK_FLAG_KEY); } catch { /* ignore */ }
                Evisio2SdkLoader.kickoff();
            }
            this._bootReal();
        });
    }

    // Real-mode boot — identical to the pre-simulator flow. Extracted into a method
    // so the postCreate role-check chain can call it after deciding the mock branch
    // isn't taking over.
    private _bootReal(): void {
        this.StandbyStart();
        this.hasUserRoleAsync("Evisio2_GeneralAccess").then((hasAccess:boolean)=>{
            if (hasAccess){
                // One-time, fire-and-forget: warn (once per browser) if the server's IIS app
                // pool isn't configured for the function relay. Never throws, never blocks boot.
                Evisio2ServerConfigCheck.runServerConfigCheckOnce();
                // Wait for SDK before instantiating the iframe — its boot wires
                // into the SDK on the parent side. .catch swallows so a failed
                // SDK load still allows the iframe to come up in degraded mode
                // (no AI-assistant integration but the inner webtool works).
                // The loader logs the warning internally.
                Evisio2SdkLoader.ready().catch(()=>{ /* logged in loader */ }).then(()=>{
                    this.StandbyStop();
                    if (this.WorkspaceCore == null){
                        this.WorkspaceCore = new evisio2IframeWorkspaceApp({
                            onSignInComplete: lang.hitch(this, this._onIframeSignedIn),
                        });
                        this.WorkspaceCore.placeAt(this.iframeWidgetContainer);
                    }
                    if (this.companionWidget == null){
                        this.companionWidget = new evisio2Companion({ continueAtPath: this.continueAtPath });
                        this.companionWidget.placeAt(this.companionContainer);
                    }
                    // Dev hotkey only registered for _DEV-role users; safely a no-op otherwise.
                    this._attachHotkeyToEgoIframe();
                });
            }else{
                this.StandbyStop();
                let noAccessTxt = this.getTranslation("Evisio2_NoAccess");
                let noAccessWrapper = document.createElement("div");
                noAccessWrapper.className = "noAccessMessage";
                let fancyH1 = document.createElement("h1");
                fancyH1.innerHTML = noAccessTxt;
                noAccessWrapper.appendChild(fancyH1);
                this.iframeWidgetContainer.appendChild(noAccessWrapper);
            }
        });
    }

    // Dev-only mock-mode boot. Skips MDSC sign-in and the real AI iframe; installs the
    // mock SDK on window.evisio2sdk, mounts the simulator widget in place of the iframe
    // app, and wires Evisio2SdkWiring against a stub iframe (mock SDK ignores it).
    // Companion still comes up — its eGo iframe + bridge host stay real so the mock's
    // tools fire real bridge round-trips when invoked from the simulator UI.
    private async _bootMock(): Promise<void> {
        this.StandbyStart();
        try {
            let mockSdk = new MockEvisio2Sdk();
            (window as any).evisio2sdk = mockSdk;

            // Companion brings up the eGo iframe + bridge host. Bridge host is bound
            // synchronously in companion postCreate so we can grab it right after.
            if (this.companionWidget == null){
                this.companionWidget = new evisio2Companion({ continueAtPath: this.continueAtPath });
                this.companionWidget.placeAt(this.companionContainer);
            }
            // Hook keydown into the eGo iframe contentDocument too (same-origin) so the
            // hotkey works regardless of where focus sits in the page.
            this._attachHotkeyToEgoIframe();
            let bridgeHost = this.companionWidget.getBridgeHost();
            if (!bridgeHost){
                console.warn("evisio2workspaceRoot._bootMock: bridge host not bound — simulator will not exercise the bridge");
                this.StandbyStop();
                return;
            }

            // Stub iframe — mock SDK accepts iframeAttach mode but ignores the element.
            let stubIframe = document.createElement("iframe");
            stubIframe.style.display = "none";
            this.sdkWiring = new Evisio2SdkWiring(stubIframe, bridgeHost);
            try {
                await this.sdkWiring.connect();
            } catch (e){
                console.warn("evisio2workspaceRoot._bootMock: mock wiring connect failed", e);
            }

            // Mount the simulator widget after connect() so its postCreate sees a live
            // mock connection on the SDK.
            this._simulatorWidget = new Evisio2SdkSimulator({ sdk: mockSdk });
            this._simulatorWidget.placeAt(this.iframeWidgetContainer);
            this._simulatorWidget.startup();
        } finally {
            this.StandbyStop();
        }
    }

    private _installMockSdkHotkey(): void {
        if (this._mockSdkHotkeyHandler) return;
        this._mockSdkHotkeyHandler = (e: KeyboardEvent) => {
            if (e.key !== "F1") return;
            e.preventDefault();
            e.stopPropagation();
            try {
                let current = sessionStorage.getItem(MOCK_SDK_FLAG_KEY) === "1";
                if (current) sessionStorage.removeItem(MOCK_SDK_FLAG_KEY);
                else         sessionStorage.setItem(MOCK_SDK_FLAG_KEY, "1");
            } catch (ex){
                console.warn("evisio2workspaceRoot: failed to toggle mock-SDK flag", ex);
                return;
            }
            window.location.reload();
        };
        document.addEventListener("keydown", this._mockSdkHotkeyHandler, true);
        // Also bind to the eGo iframe's contentDocument (same-origin) so the hotkey
        // fires even when focus is inside the iframe. Re-attached on every iframe
        // navigation; defers until the companion has mounted via _attachHotkeyToEgoIframe.
        console.log("[evisio2workspaceRoot] dev hotkey F1 installed (toggles mock SDK + reload)");
    }

    private _attachHotkeyToEgoIframe(): void {
        if (!this._mockSdkHotkeyHandler) return;
        if (!this.companionContainer) return;
        let iframe = this.companionContainer.querySelector("iframe") as HTMLIFrameElement | null;
        if (!iframe) return;

        let handler = this._mockSdkHotkeyHandler;
        let attach = () => {
            try {
                let doc = iframe!.contentDocument;
                if (!doc) return;
                doc.addEventListener("keydown", handler, true);
            } catch (e){ /* cross-origin / sandboxed — parent doc listener still active */ }
        };
        attach(); // try immediately in case the iframe already has a contentDocument
        iframe.addEventListener("load", attach);

        // Stash the iframe + per-load handler refs so _uninstallMockSdkHotkey can clean up.
        (handler as any).__egoIframe = iframe;
        (handler as any).__egoIframeOnload = attach;
    }

    private _uninstallMockSdkHotkey(): void {
        if (!this._mockSdkHotkeyHandler) return;
        document.removeEventListener("keydown", this._mockSdkHotkeyHandler, true);
        let iframe: HTMLIFrameElement | null = (this._mockSdkHotkeyHandler as any).__egoIframe || null;
        let onload: (() => void) | null = (this._mockSdkHotkeyHandler as any).__egoIframeOnload || null;
        if (iframe){
            try {
                if (onload) iframe.removeEventListener("load", onload);
                if (iframe.contentDocument){
                    iframe.contentDocument.removeEventListener("keydown", this._mockSdkHotkeyHandler, true);
                }
            } catch { /* ignore */ }
        }
        this._mockSdkHotkeyHandler = null;
    }

    // Fires from evisio2IframeWorkspaceApp after MDSC sign-in completes. Pairs the AI iframe
    // with the bridge host (owned by the companion sibling) and stands up the SDK wiring.
    // Companion is created synchronously alongside the workspace app above, so by the time
    // sign-in completes the companion's bridge host has been bound.
    private async _onIframeSignedIn(iframe: HTMLIFrameElement): Promise<void> {
        if (!this.companionWidget){
            console.warn("evisio2workspaceRoot: companion widget missing at sign-in complete — SDK wiring skipped");
            return;
        }
        let bridgeHost = this.companionWidget.getBridgeHost();
        if (!bridgeHost){
            console.warn("evisio2workspaceRoot: bridge host not bound at sign-in complete — SDK wiring skipped");
            return;
        }
        this.sdkWiring = new Evisio2SdkWiring(iframe, bridgeHost);
        try {
            await this.sdkWiring.connect();
        } catch (e){
            console.warn("evisio2workspaceRoot: SDK connect failed — running in degraded mode (eGo iframe works, AI tools inert)", e);
        }
    }

    destroy(): void {
        this._uninstallMockSdkHotkey();
        if (this._simulatorWidget){
            try { this._simulatorWidget.destroy(); } catch { /* ignore */ }
            this._simulatorWidget = null;
        }
        if (this.sdkWiring){ this.sdkWiring.destroy(); this.sdkWiring = null; }
        let self: any = this;
        super.destroy.apply(this, arguments as any);
    }

    private isInTeamsTab(): boolean {
        try {
            if (window.parent === window.self) return false;
            let ancestors: any = (window.location as any).ancestorOrigins;
            if (ancestors && ancestors.length){
                for (let i = 0; i < ancestors.length; i++){
                    if (/teams\.(microsoft|live)\.com|teams\.cloud\.microsoft/.test(ancestors[i])) return true;
                }
            }
            let ref = document.referrer || "";
            if (/teams\.(microsoft|live)\.com|teams\.cloud\.microsoft/.test(ref)) return true;
            let params = new URLSearchParams(window.location.search);
            if (params.get("inTeams") === "1" || params.has("frameContext")) return true;
            return false;
        } catch (e){
            return false;
        }
    }

    startup(): void {
        let self: any = this;
        super.startup.apply(this, arguments as any);
    }
}

export default mdsWidget(evisio2workspaceRoot);
